import { useEffect, useState } from "react";
import useBrands from "../../../explorer/hooks/useBrands";
import { useTranslation } from "../../../explorer/hooks/useTranslation";
import CommonDialog from "../CommonDialog/CommonDialog";
import { Box, Button, Typography } from "@mui/material";
import MissingPartsWrongMaterial from "./MissingPartsWrongMaterial/MissingPartsWrongMaterial";
import ServiceProvider from "../../configuration/ServiceProvider";
import useLanguages from "../../../explorer/hooks/useLanguages";
import { useApiError } from "../../hook/useApiError";
import {
  CreateOrderWarrantyRequest,
  WarrantyItem,
  WarrantyItemForDamaged,
  WarrantyItemForMissing,
  WarrantyItemType,
  WarrantyReason,
} from "../../types";
import MissingPartsWrongMaterialSummary from "./MissingPartsWrongMaterial/MissingPartsWrongMaterialSummary";
import DamagedAndBrokenParts from "./DamagedAndBrokenParts/DamagedAndBrokenParts";
import DamagedAndBrokenPartsSummary from "./DamagedAndBrokenParts/DamagedAndBrokenPartsSummary";
import { usePartFormatter } from "../../hook/usePartFormatter";
import { useFormFields } from "../../hook/useFormfields";
import useCurrentSite from "../../hook/useCurrentSite";
import { ProgressPopup } from "../../../explorer/components/ProgressPopup/ProgressPopup";
import { useApiPersistNotification } from "../../hook/useApiPersistNotification";
import { debug } from "console";

export type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export enum WarrantyTypology {
  MissingPartsWrongMaterial,
  DamagedBrokenPartsWarranty,
}

export const BasketWarrantyDialog = (props: DialogProps) => {
  const { open, onClose } = props;
  const [value, setValue] = useState<WarrantyTypology>();
  const [items, setItems] = useState<WarrantyItem[]>([]);
  const [damagedItems, setDamagedItems] = useState<WarrantyItemForDamaged[]>(
    []
  );
  const [missingItems, setMissingItems] = useState<WarrantyItemForMissing[]>(
    []
  );
  const [errorList, setErrorList] = useState<
    {
      key: "case" | "damageType" | "received" | "expected";
      indexes: string[];
    }[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [summary, setSummary] = useState<boolean>(false);
  const [progressPopupOpen, setProgressPopupOpen] = useState(false);
  const brand = useBrands().currentBrandUrl;
  const [site] = useCurrentSite();
  const { t } = useTranslation();
  const [error, setError] = useApiError();
  const lang = useLanguages().currentLanguage;
  const partFormatter = usePartFormatter();
  const [useNotification, setUseApiNotification] = useApiPersistNotification();

  const {
    setFormFields,
    formFields,
    createChangeHandler,
    createChangeValueHandler,
  } = useFormFields({
    deliveryAddress: "",
    invoiceAddress: "",
    partialShipment: false,
    note: "",
    deliveryDate: "",
    ponReference: "",
    noteItems: {},
    hasAgreement: false,
    cgvAgreement:false,
    
  });
  const [expiredWarranty, setExpiredWarranty] = useState<boolean>(false);
  // serve per memorizzare la funzione di creazione dell'ordine di garanzia sulla conferma del dialog di warning per la garanzia scaduta
  const [confirmCreateOrderWarranty, setConfirmCreateOrderWarranty] = useState<() => void>(() => () => {});

  useEffect(() => {
    if (
      !open &&
      (value === WarrantyTypology.DamagedBrokenPartsWarranty ||
        value === WarrantyTypology.MissingPartsWrongMaterial)
    ) {
      setLoading(true);
      ServiceProvider.Warranty.getBasketDetails(brand, lang)
        .then((response) => {
          setItems(response);
        })
        .catch((error) => {
          setError(error?.data?.title);
          console.error(error);
        });
    }

    setFormFields({
      deliveryAddress: "",
      invoiceAddress: "",
      partialShipment: false,
      note: "",
      deliveryDate: "",
      ponReference: "",
      noteItems: {},
      hasAgreement: false,
      cgvAgreement:false,
    });
  }, [open, value]);

  useEffect(() => {
    setDamagedItems(items as WarrantyItemForDamaged[]);
    setMissingItems(items as WarrantyItemForMissing[]);
    setLoading(false);
  }, [items]);

  const handleClose = () => {
    setValue(undefined);
    setLoading(true);
    onClose();
  };

  const handleConfirmMissingItems = () => {
    const missingItemsFormatted = missingItems.map((x) => {
      if (x.case === WarrantyReason.MissingPart)
        x.scrappedMaterialOnSite = false;
      return x;
    });

    handleConfirm(
      missingItemsFormatted,
      WarrantyItemType.MissingPartWrongMaterial
    );
  };

  const handleConfirmDamagedItems = () => {
    handleConfirm(damagedItems, WarrantyItemType.DamagedAndBroken);
  };

  const handleConfirm = (
    itemList: WarrantyItemForMissing[] | WarrantyItemForDamaged[],
    warrantyOrderType: WarrantyItemType
  ) => {
    if (itemList.length === 0) {
      return;
    }

    if (
      formFields.deliveryAddress.trim() == "" ||
      formFields.ponReference.trim() == ""
    ) {
      setError(
        formFields.ponReference.trim() === ""
          ? t("orderoffer.pon_order_empty")
          : ""
      );
      return;
    } else {
      if (itemList.some((x) => x.outOfWarranty)) {
        setExpiredWarranty(true);
        setConfirmCreateOrderWarranty(() => () => {
          createOrderWarranty(itemList, warrantyOrderType);
          setExpiredWarranty(false);
        });
      } else {
        createOrderWarranty(itemList, warrantyOrderType);
      }
    }
  };
  
  const createOrderWarranty = (
    itemList: WarrantyItemForMissing[] | WarrantyItemForDamaged[],
    warrantyOrderType: WarrantyItemType
  ) => {
    setProgressPopupOpen(true);
    handleCloseDialogTypology();
    onClose();

    const warrantyRequest: CreateOrderWarrantyRequest =
      partFormatter.formatPartForOrderWarranty(
        itemList,
        warrantyOrderType,
        brand.toUpperCase(),
        site.customerCode ?? "",
        formFields
      );

    ServiceProvider.Warranty.createOrderWarranty(warrantyRequest, brand)
      .then((response) => {
        if (response.hasError) {
          setError(response.message);
        } else {
          setUseApiNotification(
            t("warranty.missingItemConfirmMsg").replace(
              "#CustomerOrderCode#",
              response.customerOrderCode
            )
          );
        }
      })
      .catch((error) => {
        setError(error?.data?.title);
        console.error(error);
      })
      .finally(() => {
        setProgressPopupOpen(false);
      });
      
     setProgressPopupOpen(false);
  };

  const handleCloseDialogTypology = () => {
    setValue(undefined);
    setSummary(false);
    setItems([]);
    setErrorList([]);
  };

  const handleSelectTypology = (typology: WarrantyTypology) => {
    setValue(typology);
    onClose();
  };

  const handleNextForMissing = () => {
    const newCaseErrorList = missingItems
      .filter((item) => item.case === undefined)
      .map((item) => item.rowId);

    const newExpectedErrorList = missingItems
      .filter((item) => item.expectedQuantity === null)
      .map((item) => item.rowId);

    const newRequiredErrorList = missingItems
      .filter((item) => item.quantityReceived === null)
      .map((item) => item.rowId);

    if (
      newCaseErrorList.length > 0 ||
      newRequiredErrorList.length > 0 ||
      newExpectedErrorList.length > 0
    ) {
      setErrorList([
        { key: "received", indexes: newRequiredErrorList },
        { key: "expected", indexes: newExpectedErrorList },
        { key: "case", indexes: newCaseErrorList },
      ]);
      setError(t("common.errorMissing"));
      return;
    }

    setSummary(true);
    setErrorList([]);
  };

  const handleNextForDamaged = () => {
    const newErrorList = damagedItems
      .filter((item) => item.damageType === undefined)
      .map((item) => item.rowId);

    if (newErrorList.length > 0) {
      setErrorList([{ key: "damageType", indexes: newErrorList }]);
      setError(t("common.errorDamaged"));
      return;
    }

    setSummary(true);
    setErrorList([]);
  };

  const handleBack = () => {
    setSummary(false);
  };

  return (
    <Box>
      {/* Select warranty typology */}
      <CommonDialog
        open={open}
        closeDialog={handleClose}
        dialogTitleLabel="orderoffer.warranty"
        useDenyBtn={false}
        useConfirmBtn={false}
        denyButtonText={"common.cancel"}
        confirmButtonText={"common.agree"}
        sx={{
          ".MuiDialog-container > .MuiPaper-root": { maxWidth: 700 },
        }}
      >
        <Box
          className="warranty-dialog-select-typology"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "start",
            gap: "10px",
          }}
        >
          <Button
            sx={{ flex: 1 }}
            onClick={() => {
              handleSelectTypology(WarrantyTypology.MissingPartsWrongMaterial);
            }}
            variant="outlined"
          >
            {t("common.basket.missingWrong")}
          </Button>
          <Button
            sx={{ flex: 1 }}
            onClick={() => {
              handleSelectTypology(WarrantyTypology.DamagedBrokenPartsWarranty);
            }}
            variant="outlined"
          >
            {t("common.basket.damagedAndBroken")}
          </Button>
        </Box>
      </CommonDialog>

      <CommonDialog
        maxWidth="lg"
        open={value === WarrantyTypology.MissingPartsWrongMaterial && !summary}
        closeDialog={handleCloseDialogTypology}
        confirmButton={handleNextForMissing}
        dialogTitleLabel="warranty.serviceOrder.missingWrong"
        useDenyBtn={false}
        confirmButtonText="common.next"
        disableConfirmBtn={loading}
      >
        <MissingPartsWrongMaterial
          loading={loading}
          items={missingItems}
          errorList={errorList}
        />
      </CommonDialog>

      <CommonDialog
        maxWidth="lg"
        open={value === WarrantyTypology.MissingPartsWrongMaterial && summary}
        closeDialog={handleCloseDialogTypology}
        denyButton={handleBack}
        confirmButton={handleConfirmMissingItems}
        dialogTitleLabel="warranty.serviceOrder.missingWrong"
        denyButtonText="common.back"
      >
        <MissingPartsWrongMaterialSummary
          loading={loading}
          items={missingItems}
          createChangeValueHandler={createChangeValueHandler}
          createChangeHandler={createChangeHandler}
          formFields={formFields}
        />
      </CommonDialog>

      <CommonDialog
        maxWidth="lg"
        open={value === WarrantyTypology.DamagedBrokenPartsWarranty && !summary}
        closeDialog={handleCloseDialogTypology}
        confirmButton={handleNextForDamaged}
        dialogTitleLabel="warranty.serviceOrder.damagedAndBroken"
        useDenyBtn={false}
        disableConfirmBtn={loading}
        confirmButtonText="common.next"
      >
        <DamagedAndBrokenParts
          loading={loading}
          items={damagedItems}
          errorList={errorList}
        />
      </CommonDialog>

      <CommonDialog
        maxWidth="lg"
        open={value === WarrantyTypology.DamagedBrokenPartsWarranty && summary}
        closeDialog={handleCloseDialogTypology}
        denyButton={handleBack}
        confirmButton={handleConfirmDamagedItems}
        dialogTitleLabel="warranty.serviceOrder.damagedAndBroken"
        denyButtonText="common.back"
      >
        <DamagedAndBrokenPartsSummary
          loading={loading}
          items={damagedItems}
          createChangeValueHandler={createChangeValueHandler}
          createChangeHandler={createChangeHandler}
          formFields={formFields}
        />
      </CommonDialog>

      <ProgressPopup
        open={progressPopupOpen}
        label={"orderoffer.warranty_sending"}
      />

      <CommonDialog
        maxWidth="xs"
        open={expiredWarranty ?? false}
        closeDialog={() => setExpiredWarranty(false)}
        denyButton={() => setExpiredWarranty(false)}
        confirmButton={confirmCreateOrderWarranty ?? (() => {})}
        dialogTitleLabel="alert.title.warning"
        denyButtonText="common.back"
        dialogContentLabel="orderoffer.alert.expiredWarranty"
      />
    </Box>
  );
};
